import React, { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabaseClient';
import { useSubcontractorProfile } from '@/contexts/SubcontractorProfileContext';
import { 
  Loader2, FileWarning, ShieldAlert, Lock, 
  ArrowRight, CheckCircle2, Printer, 
  Download, ScrollText, Mail, ShieldCheck,
  Info
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { motion, AnimatePresence } from 'framer-motion';
import SignaturePad from '@/components/contracts/SignaturePad';
import { useToast } from '@/components/ui/use-toast';
import useSessionEnforcer from '@/hooks/useSessionEnforcer';
import {
  CONTRACT_DETAIL_FIELDS, detailsFromProfile, detailsPresent, formatSortCode, validateContractDetails,
} from '@/lib/contractDetails';
import AmendmentReview from '@/components/contracts/AmendmentReview';
import { amendmentsRecord } from '@/lib/contractAmendments';

const AgreementGate = ({ children }) => {
  useSessionEnforcer(true); // Enforce 24h logout
  const { profile, loading: profileLoading, refreshProfile } = useSubcontractorProfile();
  const [pendingContract, setPendingContract] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSigning, setIsSigning] = useState(false);
  const [signedSuccess, setSignedSuccess] = useState(false);
  const [signatureName, setSignatureName] = useState('');
  const [signatureData, setSignatureData] = useState(null);
  const [readingStartTime] = useState(Date.now());
  const [hasScrolledToEnd, setHasScrolledToEnd] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpInput, setOtpInput] = useState('');
  const [otpVerified, setOtpVerified] = useState(false);
  const [isRequestingOtp, setIsRequestingOtp] = useState(false);
  // Confirm-your-details step. The contract prints whatever is on the profile
  // at signing time, and until this existed nothing put the address, NI,
  // licence or bank details there — five of nine signed agreements read
  // "N/A". Signing is released only once the row holds every field AND the
  // person has confirmed them this session (or previously, with no edits).
  const [details, setDetails] = useState(() => detailsFromProfile(null));
  const [detailErrors, setDetailErrors] = useState({});
  const [detailsDirty, setDetailsDirty] = useState(false);
  const [savingDetails, setSavingDetails] = useState(false);
  const [confirmedThisSession, setConfirmedThisSession] = useState(false);
  // An agreement sent as amendments (amends_contract_id set): the driver agrees
  // each change instead of re-reading the whole agreement (20261286).
  const [amendments, setAmendments] = useState([]);
  const [acceptances, setAcceptances] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [showFullText, setShowFullText] = useState(false);
  const scrollContainerRef = React.useRef(null);
  const { toast } = useToast();

  useEffect(() => {
    if (profile && profile.id) {
      checkPendingAgreements();
    } else if (!profileLoading && !profile) {
      setLoading(false);
    }
  }, [profile, profileLoading]);

  useEffect(() => {
    if (profile?.id && !detailsDirty) setDetails(detailsFromProfile(profile));
  }, [profile, detailsDirty]);

  const detailsConfirmed = Boolean(
    profile && detailsPresent(profile) && !detailsDirty &&
    (confirmedThisSession || profile.contract_details_confirmed_at),
  );

  const setDetail = (key) => (e) => {
    const raw = e.target.value;
    setDetails((d) => ({ ...d, [key]: key === 'sort_code' ? formatSortCode(raw) : raw }));
    setDetailErrors((errs) => { const n = { ...errs }; delete n[key]; return n; });
    setDetailsDirty(true);
  };

  const handleConfirmDetails = async () => {
    const check = validateContractDetails(details);
    setDetailErrors(check.errors);
    if (!check.ok) return;
    setSavingDetails(true);
    try {
      // Server-side, own row only, same rules as validateContractDetails —
      // that copy is the explanation, this one is the guard.
      const { data, error } = await supabase.rpc('subcontractor_confirm_contract_details', { p: details });
      if (error) throw error;
      if (!data?.success) {
        const list = Array.isArray(data?.errors) ? data.errors : ['The details were not accepted.'];
        toast({ title: 'Please check your details', description: list.join(' '), variant: 'destructive' });
        return;
      }
      setDetailsDirty(false);
      setConfirmedThisSession(true);
      await refreshProfile?.();
      toast({ title: 'Details confirmed', description: 'The agreement below now shows them. You can verify your identity and sign.' });
    } catch (err) {
      toast({ title: 'Could not save your details', description: err.message, variant: 'destructive' });
    } finally {
      setSavingDetails(false);
    }
  };

  const checkPendingAgreements = async () => {
    try {
      const { data, error } = await supabase
        .from('subcontractor_contracts')
        .select(`
          id,
          status,
          amends_contract_id,
          contract_template_id,
          contract_templates (
            id,
            title,
            legal_text,
            version_label,
            version_highlights
          ),
          custom_values
        `)
        .eq('subcontractor_id', profile.id)
        .eq('status', 'pending')
        .maybeSingle();

      if (error) throw error;
      setPendingContract(data);
      if (data?.amends_contract_id) await loadAmendments(data);
    } catch (err) {
      console.error("Error checking agreements:", err);
    } finally {
      setLoading(false);
    }
  };

  const loadAmendments = async (contract = pendingContract) => {
    const [a, x, q] = await Promise.all([
      supabase.from('contract_amendments').select('*').eq('contract_template_id', contract.contract_template_id).order('position'),
      supabase.from('contract_amendment_acceptances').select('amendment_id, agreed_at').eq('subcontractor_contract_id', contract.id),
      supabase.from('contract_amendment_questions').select('id, amendment_id, question, answer, asked_at').eq('subcontractor_contract_id', contract.id).order('asked_at'),
    ]);
    setAmendments(a.data || []);
    setAcceptances(x.data || []);
    setQuestions(q.data || []);
    return x.data || [];
  };

  const isAmendment = Boolean(pendingContract?.amends_contract_id);
  const agreedIds = new Set(acceptances.map((x) => x.amendment_id));
  const allAgreed = amendments.length > 0 && amendments.every((a) => agreedIds.has(a.id));
  // What must be read before the code can be requested: every change, or the whole document.
  const hasRead = isAmendment ? allAgreed : hasScrolledToEnd;

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    // Allow a small margin of error (5px)
    if (scrollTop + clientHeight >= scrollHeight - 5) {
      setHasScrolledToEnd(true);
    }
  };

  const handleRequestOtp = async () => {
    setIsRequestingOtp(true);
    try {
      const { error } = await supabase.functions.invoke('notify-subcontractor-agreement', {
        body: { action: 'otp', subcontractorId: profile.id }
      });
      if (error) throw error;
      setOtpSent(true);
      toast({ title: "Verification Sent", description: "A 4-digit security code has been sent to your email." });
    } catch (err) {
      toast({ title: "Request Failed", description: err.message, variant: "destructive" });
    } finally {
      setIsRequestingOtp(false);
    }
  };

  const handleVerifyOtp = async () => {
    setIsRequestingOtp(true);
    try {
      // Checked by the server: the code itself is never readable (20261286).
      const { data: ok, error } = await supabase.rpc('subcontractor_contract_verify_otp', {
        p_contract_id: pendingContract.id, p_code: otpInput,
      });
      if (error) throw error;
      if (ok) {
        setOtpVerified(true);
        toast({ title: "Identity Verified", className: "bg-green-600 text-white" });
      } else {
        toast({ title: "Invalid Code", description: "The verification code is incorrect.", variant: "destructive" });
      }
    } catch (err) {
      toast({ title: "Verification Error", description: err.message, variant: "destructive" });
    } finally {
      setIsRequestingOtp(false);
    }
  };

  const parseTemplate = (text) => {
    if (!text) return "";
    let parsed = text;
    
    // 1. Create tokens from ALL profile columns (e.g., {{VAT_NUMBER}}, {{BILLING_ADDRESS}})
    const profileTokens = Object.entries(profile || {}).reduce((acc, [key, value]) => {
      if (typeof value === 'string' || typeof value === 'number') {
        acc[key.toUpperCase()] = value || "N/A";
      }
      return acc;
    }, {});

    // 2. Computed tokens for convenience and high-priority Aliases
    const today = new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
    const computedTokens = {
      // Name Mapping
      FIRST_NAME: profile?.first_name || "N/A",
      LAST_NAME: profile?.last_name || "N/A",
      FULL_NAME: profile?.full_name || `${profile?.first_name || ''} ${profile?.last_name || ''}`.trim() || profile?.name || "N/A",
      
      // Identity & Contact
      EMAIL: profile?.user_email || profile?.email || "N/A",
      EMAIL_ADDRESS: profile?.user_email || profile?.email || "N/A",
      NATIONAL_INSURANCE: profile?.national_insurance || "N/A",
      DRIVER_LICENSE: profile?.driver_license || "N/A",
      DRIVERS_LICENSE: profile?.driver_license || "N/A",
      
      // Banking (Confirmed Working, but preserved)
      BANK_NAME: profile?.bank_name || "N/A",
      ACCOUNT_NUMBER: profile?.account_number || "N/A",
      SORT_CODE: profile?.sort_code || "N/A",
      
      // Date Aliases (Ensures alignment with template naming)
      DATE: today,
      TODAY: today,
      CURRENT_DATE: today,
      
      // Legacy Support
      COMPANY_NAME: profile?.company_name || profile?.full_name || "N/A",
    };

    // 3. Merge hierarchy: Profile Fields < Admin Manual Overrides < Computed (Automation wins)
    const combinedTokens = {
      ...profileTokens,
      ...(pendingContract.custom_values || {}),
      ...computedTokens
    };

    Object.keys(combinedTokens).forEach(key => {
      const regex = new RegExp(`{{${key}}}`, 'g');
      parsed = parsed.replace(regex, combinedTokens[key] || "N/A");
    });
    return parsed;
  };

  const handlePrint = () => {
    // Basic print: open current content in a new window for printing
    const printContent = parseTemplate(pendingContract.contract_templates.legal_text);
    const printWindow = window.open('', '_blank');
    const fullLegalTitle = `${pendingContract.contract_templates.title} - ${profile.full_name || profile.name}`;
    
    printWindow.document.write(`
      <html>
        <head>
          <title>${fullLegalTitle}</title>
          <style>
            body { font-family: serif; padding: 40px; line-height: 1.6; color: #000; }
            h1 { text-align: center; font-family: sans-serif; font-size: 24px; border-bottom: 2px solid #000; padding-bottom: 15px; margin-bottom: 30px; }
            .content { white-space: pre-wrap; margin-bottom: 40px; }
            .audit-trail { margin-top: 50px; border-top: 1px solid #e2e8f0; padding-top: 20px; font-size: 9px; font-family: sans-serif; color: #64748b; }
            .audit-row { display: flex; justify-content: space-between; margin-bottom: 4px; }
          </style>
        </head>
        <body>
          <h1>${fullLegalTitle}</h1>
          <div class="content">${printContent}</div>
          
          <div class="audit-trail">
            <div class="audit-row">
              <span><strong>ELECTRONIC RECORD:</strong> ${pendingContract.id}</span>
              <span><strong>VERSION:</strong> ${pendingContract.contract_templates.version_label}</span>
            </div>
            <div class="audit-row">
              <span><strong>EXECUTED BY:</strong> ${profile.full_name}</span>
              <span><strong>TIMESTAMP:</strong> ${new Date().toLocaleString()}</span>
            </div>
            <div class="audit-row">
              <span><strong>DIGITAL CERTIFICATION:</strong> This document was electronically signed and verified via Email OTP.</span>
            </div>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 500);
  };

  const handleSign = async () => {
    if (!signatureName.trim()) {
      toast({ title: "Name Required", description: "Please type your full legal name.", variant: "destructive" });
      return;
    }
    if (!signatureData) {
      toast({ title: "Signature Required", description: "Please sign in the box using your finger or mouse.", variant: "destructive" });
      return;
    }
    
    setIsSigning(true);
    try {
      const readingTime = Math.round((Date.now() - readingStartTime) / 1000);
      const ua = navigator.userAgent;

      // 1. Get IP Address
      let ip = "0.0.0.0";
      try {
        const ipRes = await fetch('https://api.ipify.org?format=json');
        const ipData = await ipRes.json();
        ip = ipData.ip;
      } catch (e) {
        console.warn("IP tracking failed, proceeding with fallback.");
      }

      const auditTrail = `
--- LEGAL AUDIT TRAIL ---
IP ADDRESS: ${ip}
TIMESTAMP: ${new Date().toISOString()}
READING TIME: ${readingTime} seconds
BROWSER: ${ua}
ELECTRONIC OTP: VERIFIED
-------------------------
`;
      // An amendment contract also records which changes were agreed, and when.
      const agreedRecord = isAmendment ? `\n${amendmentsRecord(amendments, await loadAmendments())}\n` : '';
      const finalizedText = parseTemplate(pendingContract.contract_templates.legal_text) + "\n" + agreedRecord + auditTrail;

      // 2. Prepare Signature Image (Base64 to Blob)
      const base64Data = signatureData.split(',')[1];
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'image/png' });

      // 3. Upload to Storage
      const fileName = `${profile.id}_${pendingContract.id}_${Date.now()}.png`;
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('agreement-signatures')
        .upload(fileName, blob);

      if (uploadError) throw uploadError;

      // 4. Update Database
      const { error } = await supabase
        .from('subcontractor_contracts')
        .update({
          status: 'signed',
          signed_at: new Date().toISOString(),
          immutable_snapshot: finalizedText,
          digital_footprint_name: signatureName.trim(),
          signature_url: uploadData.path,
          ip_address: ip,
          reading_time_seconds: readingTime,
          browser_info: ua,
          otp_verified: true
        })
        .eq('id', pendingContract.id);

      if (error) throw error;
      
      // 5. Trigger Confirmation Email (Don't await to avoid UI blocking)
      supabase.functions.invoke('notify-subcontractor-agreement', {
        body: { action: 'confirm', subcontractorId: profile.id, contractId: pendingContract.id }
      }).catch(err => console.warn("Failed to send confirmation email:", err));
      
      setSignedSuccess(true);
      setTimeout(() => {
        setPendingContract(null);
      }, 5000); // Give user time to see the download button
    } catch (err) {
      console.error("Signing failed:", err);
      toast({ title: "Submission Failed", description: err.message, variant: "destructive" });
    } finally {
      setIsSigning(false);
    }
  };

  if (loading || profileLoading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-white text-slate-900">
        <Loader2 className="h-10 w-10 animate-spin text-primary mb-4" />
        <p className="text-[10px] font-black uppercase tracking-widest opacity-40">Verifying agreement status...</p>
      </div>
    );
  }

  if (pendingContract) {
    return (
      <div className="min-h-screen bg-white text-slate-900 overflow-y-auto selection:bg-primary/20 font-sans">
        <div className="max-w-4xl mx-auto py-16 px-6">
          <AnimatePresence mode="wait">
            {!signedSuccess ? (
              <motion.div
                key="signing-view"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.98 }}
                className="space-y-12"
              >
                {/* Header */}
                <div className="space-y-4">
                  <div className="flex items-center gap-2 px-3 py-1 w-fit rounded-lg bg-slate-100 border border-slate-200 text-slate-600">
                    <Lock size={12} />
                    <span className="text-[10px] font-black uppercase tracking-[0.1em]">Verification Required</span>
                  </div>
                  <h1 className="text-4xl md:text-5xl font-black tracking-tight text-slate-950">
                    {isAmendment ? <>Your agreement has been <span className="text-primary">updated</span></> : <>Service <span className="text-primary">Agreement</span></>}
                  </h1>
                  <p className="text-slate-500 font-medium max-w-2xl leading-relaxed">
                    {isAmendment
                      ? `There are ${amendments.length} changes to the agreement you signed. Read each one and agree it, or ask a question. You don't need to read the whole agreement again. Once every change is agreed and signed, your full dashboard access is restored.`
                      : 'A prompt action is required to review and sign your updated service agreement. Once signed, your full dashboard access will be immediately restored.'}
                  </p>
                </div>

                {isAmendment && (
                  <AmendmentReview
                    contractId={pendingContract.id}
                    amendments={amendments}
                    acceptances={acceptances}
                    questions={questions}
                    onChange={() => loadAmendments()}
                  />
                )}
                {isAmendment && (
                  <button type="button" onClick={() => setShowFullText((v) => !v)}
                    className="text-sm font-bold text-primary underline-offset-4 hover:underline">
                    {showFullText ? 'Hide the full updated agreement' : 'Read the full updated agreement (optional)'}
                  </button>
                )}

                {/* Contract Body */}
                {(!isAmendment || showFullText) && (
                <div className="rounded-3xl bg-white border border-slate-200 shadow-sm overflow-hidden">
                  <div className="p-6 md:p-8 bg-slate-50/50 border-b border-slate-200 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-primary rounded-lg text-white">
                        <FileWarning size={20} />
                      </div>
                      <div>
                        <p className="text-[10px] font-bold uppercase tracking-widest text-primary">Legal Review</p>
                        <h3 className="font-bold text-slate-900 tracking-tight">{pendingContract.contract_templates.title}</h3>
                      </div>
                    </div>
                    <Badge variant="outline" className="text-[10px] font-bold text-slate-400 border-slate-200">{pendingContract.contract_templates.version_label}</Badge>
                  </div>
                  <div className="p-8 md:p-12 h-[550px] overflow-y-auto bg-white font-serif text-[1.1rem] leading-relaxed text-slate-800 premium-scrollbar whitespace-pre-wrap"
                       onScroll={handleScroll}
                       ref={scrollContainerRef}>
                    
                    {/* Version Highlights / What's New */}
                    {pendingContract.contract_templates.version_highlights && (
                      <div className="mb-10 p-6 rounded-2xl bg-violet-50 border border-violet-100 font-sans text-sm">
                        <div className="flex items-center gap-2 text-violet-600 font-black uppercase tracking-widest mb-3">
                          <Info size={14} /> What's New In This Version
                        </div>
                        <p className="text-slate-600 leading-relaxed font-medium">
                          {pendingContract.contract_templates.version_highlights}
                        </p>
                      </div>
                    )}

                    {parseTemplate(pendingContract.contract_templates.legal_text)}

                    {/* End of document marker to assist the scroll logic visually */}
                    <div className="mt-20 pt-10 border-t border-slate-100 flex flex-col items-center text-slate-300">
                        <ScrollText size={32} className="mb-2" />
                        <span className="text-[10px] font-bold uppercase tracking-[0.3em]">End of Agreement</span>
                    </div>
                  </div>
                </div>
                )}

                {/* Confirm your details — the values the agreement prints */}
                <div className="border-t border-slate-100 pt-12">
                  <div className="flex items-start gap-4 mb-6">
                    <div className={`p-3 rounded-2xl ${detailsConfirmed ? 'bg-green-600 text-white' : 'bg-primary text-white'}`}>
                      {detailsConfirmed ? <CheckCircle2 size={24} /> : <Info size={24} />}
                    </div>
                    <div>
                      <h4 className="font-bold text-slate-900">Your details on this agreement</h4>
                      <p className="text-xs text-slate-500 font-medium max-w-2xl">
                        These are printed into the document above and used to pay you. Your address and licence number come from your
                        verified ID where we have them — check they are right, add anything missing, then confirm.
                        {detailsConfirmed ? ' Confirmed.' : ' Signing unlocks once they are confirmed.'}
                      </p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    {CONTRACT_DETAIL_FIELDS.map((f) => (
                      <div key={f.key} className={`space-y-2 ${f.kind === 'address' ? 'md:col-span-2' : ''}`}>
                        <Label className="text-[10px] font-black uppercase tracking-widest text-slate-400">{f.label}</Label>
                        {f.kind === 'address' ? (
                          <textarea
                            rows={2}
                            value={details[f.key] || ''}
                            onChange={setDetail(f.key)}
                            disabled={savingDetails}
                            placeholder="House number, street, town, postcode"
                            className={`w-full bg-slate-50 border h-auto rounded-xl px-5 py-3 font-semibold text-sm focus:ring-2 focus:ring-primary/20 outline-none transition-all placeholder:text-slate-300 ${detailErrors[f.key] ? 'border-rose-300' : 'border-slate-200'}`}
                          />
                        ) : (
                          <input
                            type="text"
                            inputMode={f.key === 'sort_code' || f.key === 'account_number' ? 'numeric' : 'text'}
                            autoComplete="off"
                            value={details[f.key] || ''}
                            onChange={setDetail(f.key)}
                            disabled={savingDetails}
                            placeholder={f.key === 'sort_code' ? '12-34-56' : f.key === 'account_number' ? '8 digits' : f.key === 'national_insurance' ? 'QQ123456C' : ''}
                            className={`w-full bg-slate-50 border h-12 rounded-xl px-5 font-semibold text-sm focus:ring-2 focus:ring-primary/20 outline-none transition-all placeholder:text-slate-300 ${detailErrors[f.key] ? 'border-rose-300' : 'border-slate-200'}`}
                          />
                        )}
                        {detailErrors[f.key] && <p className="text-[11px] font-semibold text-rose-500">{detailErrors[f.key]}</p>}
                      </div>
                    ))}
                  </div>
                  <div className="mt-5 flex flex-wrap items-center gap-3">
                    <Button
                      onClick={handleConfirmDetails}
                      disabled={savingDetails || (detailsConfirmed && !detailsDirty)}
                      className="h-12 rounded-2xl px-6 font-bold"
                    >
                      {savingDetails ? <Loader2 className="animate-spin" /> : detailsConfirmed && !detailsDirty ? <><CheckCircle2 className="mr-2" size={16} /> Confirmed</> : 'Confirm these details'}
                    </Button>
                    {detailsDirty && <span className="text-[11px] font-semibold text-amber-600">Unsaved changes — confirm to update the agreement.</span>}
                  </div>
                </div>

                {/* Signature Block */}
                <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-start border-t border-slate-100 pt-12">
                  <div className="md:col-span-5 space-y-6">
                    <div className="space-y-2">
                        <Label className="text-[10px] font-black uppercase tracking-widest text-slate-400">Electronic Acknowledgment</Label>
                        <input 
                          type="text"
                          placeholder="Type your full legal name..."
                          className="w-full bg-slate-50 border border-slate-200 h-14 rounded-xl px-5 font-bold text-lg focus:ring-2 focus:ring-primary/20 outline-none transition-all placeholder:text-slate-300"
                          value={signatureName}
                          onChange={(e) => setSignatureName(e.target.value)}
                        />
                    </div>
                    
                    <div className="p-5 rounded-2xl bg-slate-50 border border-slate-200 text-[11px] text-slate-500 font-semibold leading-relaxed">
                        By typing your name and providing your handwritten signature, you agree that this satisfies the legal requirement for your authorization.
                    </div>
                  </div>

                  <div className="md:col-span-12 lg:col-span-7 space-y-5">
                    {!otpVerified ? (
                       <div className="p-8 rounded-[2rem] bg-slate-50 border border-slate-200 border-dashed space-y-6">
                          <div className="flex items-center gap-4">
                             <div className={`p-3 rounded-2xl ${hasRead && detailsConfirmed ? 'bg-primary text-white' : 'bg-slate-200 text-slate-400'}`}>
                                <ShieldCheck size={24} />
                             </div>
                             <div>
                                <h4 className="font-bold text-slate-900">Identity Verification</h4>
                                <p className="text-xs text-slate-500 font-medium">{!hasRead ? (isAmendment ? 'Agree each change above to proceed.' : 'Please scroll and read the entire document to proceed.') : !detailsConfirmed ? 'Confirm your details above to unlock signing.' : 'Document reviewed. Verify your identity to sign.'}</p>
                             </div>
                          </div>

                          {!otpSent ? (
                            <Button 
                              disabled={!hasRead || !detailsConfirmed || isRequestingOtp}
                              onClick={handleRequestOtp}
                              className="w-full h-14 rounded-2xl bg-white border-2 border-slate-200 text-slate-700 hover:bg-slate-50 hover:border-primary/30 font-bold transition-all"
                            >
                              {isRequestingOtp ? <Loader2 className="animate-spin" /> : <><Mail className="mr-2" size={18} /> Request Identity PIN</>}
                            </Button>
                          ) : (
                            <div className="space-y-4">
                               <div className="flex gap-2">
                                  <Input 
                                    placeholder="Enter 4-digit code..."
                                    className="h-14 rounded-xl text-center text-2xl font-black tracking-[0.5em] bg-white"
                                    maxLength={4}
                                    value={otpInput}
                                    onChange={(e) => setOtpInput(e.target.value)}
                                  />
                                  <Button 
                                    className="h-14 px-6 rounded-xl bg-primary text-white"
                                    onClick={handleVerifyOtp}
                                    disabled={otpInput.length < 4 || isRequestingOtp}
                                  >
                                    {isRequestingOtp ? <Loader2 className="animate-spin" /> : 'Verify'}
                                  </Button>
                               </div>
                               <button onClick={handleRequestOtp} className="text-[10px] uppercase font-bold text-slate-400 hover:text-primary tracking-widest w-full">Resend Code</button>
                            </div>
                          )}
                       </div>
                    ) : (
                      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-5">
                        <div className="space-y-2">
                            <Label className="text-[10px] font-black uppercase tracking-widest text-primary flex items-center gap-2">
                               <ShieldCheck size={12} /> Handwritten Signature Unlocked
                            </Label>
                            <div className="rounded-2xl border-2 border-primary/20 overflow-hidden bg-white shadow-xl shadow-primary/5 transition-all">
                              <SignaturePad onSave={setSignatureData} />
                            </div>
                        </div>

                        <Button 
                            disabled={isSigning || !signatureName.trim() || !signatureData}
                            onClick={handleSign}
                            className="w-full h-14 rounded-xl bg-primary hover:bg-primary/90 text-white font-black text-lg uppercase tracking-widest shadow-lg shadow-primary/20 transition-all active:scale-[0.98]"
                        >
                            {isSigning ? (
                              <Loader2 className="animate-spin" />
                            ) : (
                              <>Sign & Unlock <ArrowRight className="ml-3" size={20} /></>
                            )}
                        </Button>
                      </motion.div>
                    )}
                  </div>
                </div>
              </motion.div>
            ) : (
              <motion.div 
                key="success-view"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-white border border-green-200 rounded-[3rem] p-16 flex flex-col items-center text-center space-y-8 shadow-2xl"
              >
                <div className="h-24 w-24 rounded-full bg-green-50 flex items-center justify-center text-green-600 border border-green-200">
                  <CheckCircle2 size={48} />
                </div>
                <div className="space-y-4">
                  <h2 className="text-5xl font-black tracking-tight leading-none text-slate-950">Agreement <span className="text-green-600">Secured</span></h2>
                  <p className="text-slate-500 text-lg font-medium max-w-sm mx-auto">
                    Your digital signature has been recorded and encrypted. The portal is now unlocking.
                  </p>
                </div>

                <div className="pt-4">
                  <p className="text-sm font-bold text-slate-400 uppercase tracking-widest animate-pulse">
                    Transitioning to Dashboard...
                  </p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    );
  }

  return children;
};

// Internal mini-badge/component if not available
const Badge = ({ children, className }) => (
  <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors ${className}`}>
    {children}
  </span>
);

export default AgreementGate;
